import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

export default function Marketing() {
  const marketingSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Digital Marketing Services",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Perfect Signs Canada",
      "telephone": "+1-780-554-3949",
      "email": "info@perfectsignscanada.com",
      "address": {
        "@type": "PostalAddress",
        "streetAddress": "6109 176 Ave NW",
        "addressLocality": "Edmonton",
        "addressRegion": "AB",
        "postalCode": "T5Y 0K8",
        "addressCountry": "CA"
      }
    },
    "areaServed": "Canada",
    "serviceType": [
      "Social media marketing",
      "Google business marketing",
      "Email promotions",
      "WhatsApp campaigns",
      "YouTube marketing",
      "Local business marketing"
    ],
    "url": "https://perfectsignscanada.com/marketing.html",
    "description": "Digital marketing services for Canadian businesses, including social media, Google visibility, email promotions, WhatsApp, and YouTube content."
  };

  return (
    <>
      <SEO 
        title="Digital Marketing Services Canada | Perfect Signs"
        description="Perfect Signs Canada helps businesses grow with social media marketing, Google visibility, email promotions, WhatsApp campaigns, YouTube content, and practical digital strategy."
        keywords="digital marketing Canada, social media marketing Edmonton, Facebook marketing, Instagram marketing, Google business marketing, email promotions, WhatsApp marketing, YouTube marketing, local business marketing"
        canonical="https://perfectsignscanada.com/marketing.html"
        schema={marketingSchema}
      />

      {/* ======= Breadcrumbs ======= */}
      <div className="breadcrumbs d-flex align-items-center" style={{ backgroundImage: "url('/assets/img/about-header.jpg')" }}>
        <div className="container position-relative d-flex flex-column align-items-center" data-aos="fade-up">
          <p className="breadcrumbs-eyebrow">Marketing Services</p>
          <h2>Campaigns that connect your signs, storefront, website, and social channels.</h2>
          <p className="breadcrumbs-summary">We help local businesses stay visible with practical social media, Google, email, WhatsApp, and content marketing support.</p>
          <ol>
            <li><Link to="/">Home</Link></li>
            <li>Marketing</li>
          </ol>
        </div>
      </div>
      {/* End Breadcrumbs */}

      {/* ======= Marketing Intro Section ======= */}
      <section className="marketing-intro">
        <div className="container" data-aos="fade-up">
          <div className="row gy-4 align-items-center">
            <div className="col-lg-5" data-aos="fade-up" data-aos-delay="100">
              <img src="/assets/img/SocialMedia.jpg" className="img-fluid marketing-photo" alt="Digital marketing strategy for local businesses" />
            </div>
            <div className="col-lg-7" data-aos="fade-up" data-aos-delay="250">
              <div className="content ps-lg-5">
                <p className="section-kicker">Marketing that supports real business goals</p>
                <h3>Turn attention into visits, calls, messages, and repeat customers.</h3>
                <p>Perfect Signs Canada helps businesses build practical marketing campaigns across social media, Google, email, WhatsApp, and video channels. The goal is simple: keep your brand visible, make your offer clear, and guide customers toward the next action.</p>
                <p>Because we also work with signage and websites, we can help your marketing feel consistent everywhere customers meet you: on the road, in search, on social media, in their inbox, and at your storefront.</p>
                <ul>
                  <li><i className="bi bi-check-circle-fill text-primary"></i> Social media campaigns for awareness, promotions, and local engagement.</li>
                  <li><i className="bi bi-check-circle-fill text-primary"></i> Google, email, WhatsApp, and content support for practical lead generation.</li>
                  <li><i className="bi bi-check-circle-fill text-primary"></i> Messaging that connects your brand, signage, website, and offers.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* End Marketing Intro Section */}

      {/* ======= Marketing Channels ======= */}
      <section className="marketing-channels">
        <div className="container" data-aos="fade-up">
          <div className="section-header" data-aos="fade-up">
            <h2>Marketing Channels</h2>
            <p>Pick the right channels for your audience, offer, and budget instead of trying to be everywhere at once.</p>
          </div>
          <div className="row gy-4">
            {/* Facebook */}
            <div className="col-lg-4 col-md-6" data-aos="fade-up" data-aos-delay="100">
              <div className="marketing-card">
                <img src="/assets/img/Facebook.jpg" alt="Facebook marketing services" className="img-fluid" />
                <div>
                  <i className="bi bi-facebook"></i>
                  <h3>Facebook</h3>
                  <p>Promote offers, events, services, and updates to local audiences with clear posts and campaigns.</p>
                </div>
              </div>
            </div>

            {/* Instagram */}
            <div className="col-lg-4 col-md-6" data-aos="fade-up" data-aos-delay="200">
              <div className="marketing-card">
                <img src="/assets/img/Instagram.jpg" alt="Instagram marketing services" className="img-fluid" />
                <div>
                  <i className="bi bi-instagram"></i>
                  <h3>Instagram</h3>
                  <p>Build a visual presence with polished posts, reels, product highlights, and brand storytelling.</p>
                </div>
              </div>
            </div>

            {/* Google */}
            <div className="col-lg-4 col-md-6" data-aos="fade-up" data-aos-delay="300">
              <div className="marketing-card">
                <img src="/assets/img/Google.jpg" alt="Google business marketing services" className="img-fluid" />
                <div>
                  <i className="bi bi-google"></i>
                  <h3>Google</h3>
                  <p>Improve how customers find you through search-focused content, local visibility, and profile updates.</p>
                </div>
              </div>
            </div>

            {/* WhatsApp */}
            <div className="col-lg-4 col-md-6" data-aos="fade-up" data-aos-delay="100">
              <div className="marketing-card">
                <img src="/assets/img/Whatsapp.jpg" alt="WhatsApp marketing services" className="img-fluid" />
                <div>
                  <i className="bi bi-whatsapp"></i>
                  <h3>WhatsApp</h3>
                  <p>Use direct messaging for promotions, customer updates, appointment reminders, and quick follow-ups.</p>
                </div>
              </div>
            </div>

            {/* YouTube */}
            <div className="col-lg-4 col-md-6" data-aos="fade-up" data-aos-delay="200">
              <div className="marketing-card">
                <img src="/assets/img/Youtube.jpg" alt="YouTube marketing services" className="img-fluid" />
                <div>
                  <i className="bi bi-youtube"></i>
                  <h3>YouTube</h3>
                  <p>Explain services, show work, answer common questions, and create video content customers can trust.</p>
                </div>
              </div>
            </div>

            {/* Email */}
            <div className="col-lg-4 col-md-6" data-aos="fade-up" data-aos-delay="300">
              <div className="marketing-card">
                <img src="/assets/img/EmailPromotions.jpg" alt="Email promotion services" className="img-fluid" />
                <div>
                  <i className="bi bi-envelope-at-fill"></i>
                  <h3>Email</h3>
                  <p>Stay connected with customers through announcements, seasonal offers, newsletters, and follow-up campaigns.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* End Marketing Channels */}

      {/* ======= Marketing Process ======= */}
      <section className="marketing-process">
        <div className="container" data-aos="fade-up">
          <div className="row gy-4">
            <div className="col-lg-3 col-md-6" data-aos="fade-up" data-aos-delay="100">
              <div className="process-card">
                <span>01</span>
                <h3>Define</h3>
                <p>Clarify the audience, offer, goal, and best channels for the campaign.</p>
              </div>
            </div>
            <div className="col-lg-3 col-md-6" data-aos="fade-up" data-aos-delay="200">
              <div className="process-card">
                <span>02</span>
                <h3>Create</h3>
                <p>Prepare messaging, visuals, content, and calls to action that fit your brand.</p>
              </div>
            </div>
            <div className="col-lg-3 col-md-6" data-aos="fade-up" data-aos-delay="300">
              <div className="process-card">
                <span>03</span>
                <h3>Launch</h3>
                <p>Publish campaigns across selected channels with consistent timing and tracking.</p>
              </div>
            </div>
            <div className="col-lg-3 col-md-6" data-aos="fade-up" data-aos-delay="400">
              <div className="process-card">
                <span>04</span>
                <h3>Improve</h3>
                <p>Review response, adjust content, and keep improving the next campaign.</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* End Marketing Process */}

      {/* ======= Marketing CTA ======= */}
      <section className="marketing-cta">
        <div className="container" data-aos="fade-up">
          <div className="row align-items-center gy-4">
            <div className="col-lg-8" data-aos="fade-up" data-aos-delay="100">
              <p className="section-kicker">Ready to promote your business?</p>
              <h2>Let’s create marketing that matches your brand and brings customers closer.</h2>
              <p>Start with one campaign, one offer, or one channel. We can help shape the message, visuals, and next step.</p>
            </div>
            <div className="col-lg-4 text-lg-end" data-aos="fade-up" data-aos-delay="250">
              <Link to="/contact" className="marketing-cta-btn">Plan a Campaign</Link>
            </div>
          </div>
        </div>
      </section>
      {/* End Marketing CTA */}
    </>
  );
}
